import React from "react";
import { useNavigate } from "react-router-dom";
import { Star, Shield, MessageSquare, User, CheckCircle, Wallet } from "lucide-react";
import { useCountry } from "../contexts/CountryContext";

const JoinAsProPage = () => {
  const navigate = useNavigate();
  const { country } = useCountry();
  const isAU = country.code === 'AU';
  const prefix = country.currencyPrefix;
  const gst = country.gstLabel;
  const freeMonths = isAU ? '3 months' : '6 months';

  return (
    <div className="page join-pro-page">
      <div className="join-pro-hero">
        <h1>Grow Your Service Business with HandyHub {country.name}</h1>
        <p>Create your provider profile and connect with local customers looking for your services.</p>
        <button
          onClick={() => navigate('/signup?role=provider')}
          className="btn-primary btn-large"
          data-testid="get-started-btn"
        >
          Get Started Free
        </button>
      </div>

      <div className="provider-free-banner" data-testid="provider-free-banner">
        <span className="pfb-eyebrow">Your first {freeMonths} free</span>
        <p className="pfb-lead">Create your provider profile, receive quote requests and grow your online presence with HandyHub {country.name}.</p>
        <p className="pfb-nocommission"><CheckCircle size={16} /> No commissions on your jobs — you keep 100% of what you earn.</p>
        <button
          onClick={() => navigate('/signup?role=provider')}
          className="btn-primary btn-large"
          data-testid="free-banner-cta"
        >
          Get Started Free
        </button>
        <small className="pfb-finetext">{freeMonths} free for new providers. T&amp;Cs apply.</small>
      </div>

      <div className="benefits-section">
        <h2>Why Join HandyHub?</h2>
        <div className="benefits-grid">
          <div className="benefit-card">
            <div className="benefit-icon"><User /></div>
            <h3>Reach More Customers</h3>
            <p>Get discovered by local customers looking for your services.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon"><Shield /></div>
            <h3>Build Trust</h3>
            <p>Verification badges help you stand out and build credibility</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon"><Star /></div>
            <h3>Grow Your Reputation</h3>
            <p>Collect reviews and ratings to showcase your expertise</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon"><MessageSquare /></div>
            <h3>Direct Communication</h3>
            <p>Connect directly with customers and manage quote requests</p>
          </div>
          <div className="benefit-card" data-testid="benefit-keep-earnings">
            <div className="benefit-icon"><Wallet /></div>
            <h3>Keep 100% of What You Earn</h3>
            <p>No commissions on your jobs. What you earn from your customers is yours to keep.</p>
          </div>
        </div>
      </div>

      <div className="pricing-section">
        <h2>Simple, Transparent Pricing</h2>
        <div className="pricing-intro" data-testid="pricing-free-intro">
          <strong>Start with {freeMonths} free</strong>
          <p>No membership fee for your first {freeMonths}. After your free period, choose the plan that works for your business.</p>
          <p className="pricing-intro-nocommission"><CheckCircle size={16} /> No commissions on your jobs — keep 100% of what you earn.</p>
        </div>
        <div className="pricing-grid">
          <div className="pricing-card">
            <h3>Monthly</h3>
            <div className="price" data-testid="join-monthly-price">{prefix}49<span> + {gst} / month</span></div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '-4px', marginBottom: '8px' }}>{gst} calculated at checkout</p>
            <ul>
              <li><CheckCircle size={16} /> Unlimited quote requests</li>
              <li><CheckCircle size={16} /> Business profile</li>
              <li><CheckCircle size={16} /> Customer messaging</li>
              <li><CheckCircle size={16} /> Review management</li>
            </ul>
            <button
              onClick={() => navigate('/signup?role=provider&plan=monthly')}
              className="btn-secondary"
            >
              Start Monthly
            </button>
          </div>
          <div className="pricing-card featured">
            <div className="popular-badge" data-testid="join-yearly-badge">{country.code === 'AU' ? 'Best Value' : 'Most Popular'}</div>
            <h3>Yearly</h3>
            <div className="price" data-testid="join-yearly-price">{prefix}{country.yearlyPrice}<span> + {gst} / year</span></div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '-4px', marginBottom: '4px' }}>{gst} calculated at checkout</p>
            <div className="savings" data-testid="join-yearly-savings">{country.yearlySavingsLabel}</div>
            <ul>
              <li><CheckCircle size={16} /> Everything in Monthly</li>
              <li><CheckCircle size={16} /> Priority listing</li>
              <li><CheckCircle size={16} /> Featured badge</li>
              <li><CheckCircle size={16} /> Analytics dashboard</li>
            </ul>
            <button
              onClick={() => navigate('/signup?role=provider&plan=yearly')}
              className="btn-primary"
            >
              Start Yearly
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


export default JoinAsProPage;
